'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { PrecheckFinding } from '@/features/upload/lib/precheck';
import type { FailureReason } from '@/features/upload/lib/upload-state';

export type UploadAttemptTerminal = 'ready' | 'failed' | 'abandoned';
export type UploadAttemptSource = 'file' | 'url';

/**
 * One row of the creator's local upload history (FRONT-351). Written by the
 * recorder in `features/upload/lib/upload-history-recorder.ts` from
 * upload-store phase transitions; read by the Upload health panel.
 *
 * Lives in its own persisted store on purpose: `upload-store` entries are
 * removed 5s after `complete`, on dismiss and on delete, and its writer runs
 * at TUS-progress frequency. History must outlive the entry and never
 * re-render progress consumers.
 */
export interface UploadAttempt {
  uploadId: string;
  videoUuid: string | null;
  bunnyVideoId: string | null;
  fileName: string;
  fileSize: number;
  contentType: 'video' | 'short';
  source: UploadAttemptSource;
  startedAt: number;
  /** preparing → uploading: the BE draft exists and TUS is about to start. */
  preparedAt?: number | undefined;
  /** → processing: every byte landed on Bunny. */
  uploadedAt?: number | undefined;
  readyAt?: number | undefined;
  failedAt?: number | undefined;
  abandonedAt?: number | undefined;
  /** fileSize / (uploadedAt − preparedAt), MiB/s. Same formula as `studio_upload_tus_completed`. */
  mbPerSecond?: number | undefined;
  /** Lowest sliding-window throughput the observer reported, in Mbps. */
  mbpsObservedMin?: number | undefined;
  /** Times the upload dropped and came back (uploading -> reconnecting). */
  reconnectCount: number;
  /** Times the CREATOR pressed "Retry upload". Not tus's network attempts. */
  retryCount: number;
  /** Peak network attempts tus made on a single chunk. See ActiveUpload. */
  networkAttempts: number;
  terminal?: UploadAttemptTerminal | undefined;
  failureStage?: 'upload' | 'processing' | undefined;
  failureReason?: FailureReason | undefined;
  /** Row header from the catalog ("Upload interrupted" / "Processing failed"). */
  failureTitle?: string | undefined;
  failureMessage?: string | undefined;
  failureHint?: string | undefined;
  precheck?: PrecheckFinding[] | undefined;
  updatedAt: number;
}

interface UploadHistoryState {
  attempts: UploadAttempt[];
  upsert: (attempt: UploadAttempt) => void;
  markAbandoned: (uploadId: string, now: number) => void;
  clear: () => void;
}

export const MAX_UPLOAD_ATTEMPTS = 20;

export const useUploadHistoryStore = create<UploadHistoryState>()(
  persist(
    (set, get) => ({
      attempts: [],

      upsert: (attempt) => {
        const rest = get().attempts.filter((a) => a.uploadId !== attempt.uploadId);
        set({ attempts: [attempt, ...rest].slice(0, MAX_UPLOAD_ATTEMPTS) });
      },

      markAbandoned: (uploadId, now) => {
        const current = get().attempts.find((a) => a.uploadId === uploadId);
        if (!current || current.terminal) return;
        get().upsert({ ...current, abandonedAt: now, terminal: 'abandoned', updatedAt: now });
      },

      clear: () => set({ attempts: [] }),
    }),
    {
      name: 'taboo-upload-history',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
